/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';

interface FabricBadge {
    label: string;
    icon: string;
}

interface Fabric {
    name: string;
    tagline: string;
    description: string;
    imageUrl: string;
    shopPath: string;
    badges: FabricBadge[];
}

const FABRICS: Fabric[] = [
    {
        name: 'TruNylon',
        tagline: 'Classic. Durable. Easy to Wear.',
        description:
            'Our most time-tested material. A soft, washed nylon designed to be out in the world and worn without worry.',
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dw8502602c/landing-page/materials/Sub-1-trunylon-desktop.png',
        shopPath: '/trunylon/',
        badges: [
            { label: 'Water Repellent', icon: '💧' },
            { label: 'Durable', icon: '🛡️' },
        ],
    },
    {
        name: 'Premier Twill Nylon',
        tagline: 'Sleek. Sturdy. Polished.',
        description:
            'A smooth, shiny finish gives this fabric an elevated look. The tricot backing makes it extra durable and flexible.',
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dw32ec6eee/landing-page/materials/bg-premier-twill-nylon-fabrics101-plp-d.jpg',
        shopPath: '/premiertwill/',
        badges: [
            { label: 'Durable', icon: '🛡️' },
            { label: 'Flexible', icon: '✨' },
        ],
    },
    {
        name: 'B-Lite Nylon',
        tagline: 'Featherweight. Recycled. Ready For Anything.',
        description: 'Made from 100% recycled nylon, this ultra-lightweight fabric proves less can be more.',
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dwf3f53e71/landing-page/materials/bg-blitenylon-fabrics101-plp-d.jpg',
        shopPath: '/b-lite/',
        badges: [
            { label: 'Recycled', icon: '♻️' },
            { label: 'Lightweight', icon: '🪶' },
        ],
    },
    {
        name: 'Gloss Ripstop',
        tagline: 'Shiny. Sustainable. Sporty.',
        description:
            "Made from recycled ripstop nylon, this fabric can stand up to just about anything. It's our ready for adventure fabric.",
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dwd6855eda/landing-page/materials/Sub-3-Ripstop-desktop.jpg',
        shopPath: '/ripstop/',
        badges: [
            { label: 'Recycled', icon: '♻️' },
            { label: 'Durable', icon: '🛡️' },
        ],
    },
    {
        name: 'EcoPuff',
        tagline: 'Pillowy. Light-as-air. Sustainably Crafted.',
        description:
            "Our most eco-friendly material. A water-resistant puff fabric that's super lightweight and easy to wear.",
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dw997f7879/landing-page/materials/bg-ecopuff-fabrics101-plp-d.jpg',
        shopPath: '/ecopuff/',
        badges: [
            { label: 'Water Resistant', icon: '💧' },
            { label: 'Lightweight', icon: '🪶' },
            { label: 'Eco-Friendly', icon: '🌿' },
        ],
    },
    {
        name: 'Heathered Heritage',
        tagline: 'Time-tested. Two-toned. Tenacious.',
        description:
            'Our heritage fabric has earned its name. Its textured finish and durable construction bring character and style to every bag.',
        imageUrl:
            'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default/dw33e80d42/landing-page/materials/bg-heatheredheritage-fabrics101-customerlander-d.jpg',
        shopPath: '/heritage/',
        badges: [{ label: 'Durable', icon: '🛡️' }],
    },
];

function FabricCard({ fabric, index }: { fabric: Fabric; index: number }): ReactElement {
    const isEven = index % 2 === 0;
    return (
        <div
            className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-0 overflow-hidden rounded-2xl bg-[#F5F3F0]`}>
            {/* Image */}
            <div className="md:w-1/2 aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                    src={fabric.imageUrl}
                    alt={fabric.name}
                    className="w-full h-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                />
            </div>

            {/* Content */}
            <div className="md:w-1/2 flex flex-col justify-center px-8 py-10 md:px-12 md:py-14">
                <h2
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-3xl md:text-4xl font-light tracking-wide text-[#1a1a1a] mb-2">
                    {fabric.name}
                </h2>
                <p className="text-sm font-medium tracking-[0.08em] uppercase text-[#5a5a5a] mb-4">{fabric.tagline}</p>
                <p className="text-[#5a5a5a] leading-relaxed text-sm mb-6">{fabric.description}</p>

                {/* Badges */}
                {fabric.badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                        {fabric.badges.map((badge) => (
                            <span
                                key={badge.label}
                                className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.06em] uppercase border border-[#d0cdc9] rounded-full px-3 py-1.5 text-[#1a1a1a] bg-background">
                                <span aria-hidden="true">{badge.icon}</span>
                                {badge.label}
                            </span>
                        ))}
                    </div>
                )}

                <Link
                    to={fabric.shopPath}
                    className="self-start inline-flex items-center gap-2 text-sm font-medium tracking-[0.1em] uppercase text-[#1a1a1a] border-b border-[#1a1a1a] pb-0.5 hover:opacity-60 transition-opacity">
                    Shop {fabric.name}
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                </Link>
            </div>
        </div>
    );
}

export default function Materials(): ReactElement {
    return (
        <div className="pb-20">
            <SeoMeta
                title="Our Materials — Baggallini"
                description="Explore Baggallini's thoughtfully sourced fabrics — TruNylon, Premier Twill, B-Lite, Gloss Ripstop, EcoPuff, and Heathered Heritage — each designed for superior quality and functionality."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Materials</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            {/* Hero header */}
            <div className="max-w-screen-md mx-auto px-6 pt-10 pb-12 text-center">
                <h1
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-5xl md:text-6xl font-light tracking-wide mb-4 text-[#1a1a1a]">
                    Fabricated to Function
                </h1>
                <p className="text-[#5a5a5a] max-w-lg mx-auto leading-relaxed">
                    Functionality is at the heart of everything we do. Each of our fabrics are thoughtfully sourced to
                    deliver bags with superior quality and functionality.
                </p>
            </div>

            {/* Fabric cards */}
            <div className="max-w-screen-xl mx-auto px-4 space-y-6">
                {FABRICS.map((fabric, index) => (
                    <FabricCard key={fabric.name} fabric={fabric} index={index} />
                ))}
            </div>
        </div>
    );
}
